import React, { useState, useMemo } from 'react';
import { API_BASE_URL } from '../config';

interface BookTicketsFormProps {
  events: any[];
  customers: any[];
  onSuccess: () => void;
  onError: (message: string) => void;
}

const BookTicketsForm: React.FC<BookTicketsFormProps> = ({ events, customers, onSuccess, onError }) => {
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [numberOfAdults, setNumberOfAdults] = useState(0);
  const [numberOfKids, setNumberOfKids] = useState(0);
  const [mode, setMode] = useState<'interac' | 'cash' | 'complimentary'>('interac');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Filter customers based on search
  const filteredCustomers = useMemo(() => {
    if (!customerSearch) return customers;
    const searchLower = customerSearch.toLowerCase();
    return customers.filter(c => 
      c.customer_name.toLowerCase().includes(searchLower) || 
      c.email.toLowerCase().includes(searchLower)
    );
  }, [customers, customerSearch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedEventId || !selectedCustomerId) {
      onError('Please select both an event and a customer');
      return;
    }

    if (numberOfAdults === 0 && numberOfKids === 0) {
      onError('Please select at least one ticket (adult or kid)');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/tickets/book`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          event_id: selectedEventId,
          customer_id: selectedCustomerId,
          adults: numberOfAdults,
          kids: numberOfKids,
          mode,
          confirmed
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to book ticket');
      }

      // Reset form
      setSelectedEventId(null);
      setSelectedCustomerId(null);
      setCustomerSearch('');
      setNumberOfAdults(0);
      setNumberOfKids(0);
      setMode('interac');
      setConfirmed(false);

      onSuccess();
    } catch (error: any) {
      onError(error.message || 'Failed to book ticket');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-lg p-8">
      <h2 className="text-2xl font-bold text-primary-800 mb-6">Book Tickets</h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Event Selection */}
        <div>
          <label className="block text-sm font-semibold mb-2 text-primary-700">Select Event *</label>
          <select
            value={selectedEventId || ''}
            onChange={e => setSelectedEventId(e.target.value ? parseInt(e.target.value) : null)}
            className="w-full px-4 py-3 border-2 border-primary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            required
          >
            <option value="">-- Choose an Event --</option>
            {events.map(ev => (
              <option key={ev.id} value={ev.id}>{ev.title}</option>
            ))}
          </select>
        </div>

        {/* Customer Selection */}
        <div>
          <label className="block text-sm font-semibold mb-2 text-primary-700">Select Customer *</label>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={customerSearch}
            onChange={e => setCustomerSearch(e.target.value)}
            className="w-full px-4 py-3 border-2 border-primary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent mb-2"
          />
          <select
            value={selectedCustomerId || ''}
            onChange={e => setSelectedCustomerId(e.target.value ? parseInt(e.target.value) : null)}
            className="w-full px-4 py-3 border-2 border-primary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            required
            size={5}
          >
            <option value="">-- Choose a Customer --</option>
            {filteredCustomers.map(c => (
              <option key={c.id} value={c.id}>
                {c.customer_name} ({c.email})
              </option>
            ))}
          </select>
        </div>

        {/* Number of Adults */}
        <div>
          <label className="block text-sm font-semibold mb-2 text-primary-700">Number of Adults</label>
          <input
            type="number"
            min="0"
            value={numberOfAdults}
            onChange={e => setNumberOfAdults(parseInt(e.target.value) || 0)}
            className="w-full px-4 py-3 border-2 border-primary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Number of Kids */}
        <div>
          <label className="block text-sm font-semibold mb-2 text-primary-700">Number of Kids</label>
          <input
            type="number"
            min="0"
            value={numberOfKids}
            onChange={e => setNumberOfKids(parseInt(e.target.value) || 0)}
            className="w-full px-4 py-3 border-2 border-primary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Mode */}
        <div>
          <label className="block text-sm font-semibold mb-2 text-primary-700">Payment Mode *</label>
          <select
            value={mode}
            onChange={e => setMode(e.target.value as 'interac' | 'cash' | 'complimentary')}
            className="w-full px-4 py-3 border-2 border-primary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            required
          >
            <option value="interac">Interac</option>
            <option value="cash">Cash</option>
            <option value="complimentary">Complimentary</option>
          </select>
        </div>

        {/* Confirmed */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="confirmed"
            checked={confirmed}
            onChange={e => setConfirmed(e.target.checked)}
            className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <label htmlFor="confirmed" className="ml-3 text-sm font-semibold text-primary-700">
            Confirmed
          </label>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-3 pt-4">
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Booking...' : 'Book Tickets'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BookTicketsForm;
